import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LoginForm } from './_partials/forms/LoginForm';
import { RegisterForm } from './_partials/forms/RegisterForm';
import { ForgotPasswordForm } from './_partials/forms/ForgotPasswordForm';
import { ResetPasswordForm } from './_partials/forms/ResetPasswordForm';
import { CreatePasswordForm } from './_partials/forms/CreatePasswordForm';
import { HelpDrawer } from './_partials/HelpDrawer';
import { HomeCarousel } from './_partials/HomeCarousel';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useAuth } from '@/lib/contexts/auth-context';
import { useTheme } from '@/lib/contexts/theme-context';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Mapping view -> route
const viewToRoute = {
    home: '/',
    login: '/login',
    register: '/register',
    'forgot-password': '/forgot-password',
    'reset-password': '/reset-password',
    'create-password': '/create-password',
};

export default function Welcome({ defaultView = 'home' }) {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const { theme } = useTheme();
    const [helpOpen, setHelpOpen] = useState(false);

    // Check if we have a reset token in the URL
    const resetToken = searchParams.get('token');
    const initialView = resetToken
        ? defaultView === 'create-password'
            ? 'create-password'
            : 'reset-password'
        : defaultView;
    const [view, setView] = useState(initialView); // 'home', 'login', 'register', 'forgot-password', 'reset-password', 'create-password'

    // Redirect to dashboard if already authenticated
    useEffect(() => {
        if (!isAuthLoading && isAuthenticated) {
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, isAuthLoading, navigate]);

    // Navigate to view and update URL
    const navigateToView = useCallback(
        (newView) => {
            setView(newView);
            const route = viewToRoute[newView] || '/';
            // Preserve token param for reset-password
            if (newView === 'reset-password' && resetToken) {
                navigate(`${route}?token=${resetToken}`, { replace: true });
            } else {
                navigate(route, { replace: true });
            }
        },
        [navigate, resetToken],
    );

    useEffect(() => {
        horodatyApi.ping().catch(() => {
            // Ping silently - errors are expected if API is down
        });
    }, []);

    // Mobile home view
    const renderMobileHome = () => (
        <div className="relative flex flex-col h-screen bg-background px-4 pt-6 pb-24 overflow-y-auto">
            {/* Logo */}
            <div className="theme-logo-container flex items-center gap-2 mb-6 shrink-0 h-6">
                <img
                    src={theme.logo}
                    alt={theme.logoAlt}
                    className="theme-logo theme-logo-mobile h-full w-full object-contain object-left"
                />
            </div>

            <div className="flex-1 flex flex-col justify-center items-center">
                {/* Hero image with stamp badge */}
                <div className="relative mx-auto mb-6 sm:mb-8 shrink-0">
                    <div className="h-[180px] w-[180px] sm:h-[262px] sm:w-[262px] relative rounded-[45px] sm:rounded-[59px] rotate-[180deg] scale-y-[-100%]">
                        <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-[45px] sm:rounded-[59px]">
                            <img
                                src="assets/images/home/home-mobile.png"
                                alt={t('welcome:photographiezEtProuvez')}
                                className="absolute h-[112.26%] left-[-6.44%] max-w-none top-[-0.58%] w-[112.05%]"
                            />
                        </div>
                        <div className="absolute bottom-0 -translate-x-5 sm:-translate-x-8 translate-y-4 sm:translate-y-6 w-[80px] h-[82px] sm:w-[122px] sm:h-[124px] bg-background rounded-full shadow-[0px_7px_30px_0px_rgba(0,0,0,0.1)] flex items-center justify-center p-2.5 sm:p-4">
                            <img
                                src="assets/images/picture.png"
                                alt=""
                                className="w-[55px] h-[55px] sm:w-[85px] sm:h-[85px] object-contain"
                            />
                        </div>
                    </div>
                </div>

                {/* Text content */}
                <div className="flex flex-col items-center text-center mb-6 sm:mb-10 shrink-0 px-2">
                    <h1
                        className="text-[24px] sm:text-[32px] leading-[32px] sm:leading-[42px] font-semibold text-foreground mb-2"
                        dangerouslySetInnerHTML={{ __html: t(theme.pages.welcome.titleKey) }}
                    ></h1>
                    <p
                        className="text-sm sm:text-base text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: t(theme.pages.welcome.taglineKey) }}
                    ></p>
                </div>

                {/* Buttons - wrap sur petits écrans */}
                <div className="flex flex-wrap gap-3 justify-center w-full shrink-0 px-2">
                    <Button
                        variant="solid"
                        color="primary"
                        onClick={() => navigateToView('login')}
                        className="px-6 sm:px-8 py-3 sm:py-3.5 rounded-2xl text-sm transition-all active:scale-[0.98]"
                    >
                        {t('welcome:seConnecter')}
                    </Button>
                    <Button
                        variant="solid"
                        color="secondary"
                        onClick={() => navigateToView('register')}
                        className="px-6 sm:px-8 py-3 sm:py-3.5 rounded-2xl text-sm transition-all active:scale-[0.98]"
                    >
                        {t('welcome:creerMonCompte')}
                    </Button>
                </div>
            </div>

            {/* Help button */}
            <Button
                variant="plain"
                color="neutral"
                onClick={() => setHelpOpen(true)}
                className="fixed bottom-6 right-5 w-14 h-14 bg-background border border-border rounded-full flex items-center justify-center hover:bg-surface transition-colors z-50"
            >
                <HelpCircle className="w-7 h-7 text-muted-foreground" />
            </Button>

            {/* Help drawer */}
            <HelpDrawer open={helpOpen} onOpenChange={setHelpOpen} />
        </div>
    );

    // Desktop home view
    const renderDesktopHome = () => (
        <div className="relative flex flex-col items-center justify-center text-center h-full">
            <div className="theme-logo-container absolute right-4 top-4 h-8">
                <img
                    src={theme.logo}
                    alt={theme.logoAlt}
                    className="theme-logo theme-logo-desktop h-full w-full object-contain object-right"
                />
            </div>
            <h2
                className="text-[32px] font-bold mb-4 leading-tight"
                dangerouslySetInnerHTML={{ __html: t(theme.pages.welcome.titleKey) }}
            ></h2>
            <p
                className="text-base leading-tight text-muted-foreground whitespace-nowrap"
                dangerouslySetInnerHTML={{ __html: t(theme.pages.welcome.taglineKey) }}
            ></p>

            <div className="flex gap-4 mt-12">
                <Button
                    variant="solid"
                    color="primary"
                    onClick={() => navigateToView('login')}
                    className="px-8 py-3.5 rounded-2xl text-sm transition-all active:scale-[0.98]"
                >
                    {t('welcome:seConnecter')}
                </Button>
                <Button
                    variant="solid"
                    color="secondary"
                    onClick={() => navigateToView('register')}
                    className="px-8 py-3.5 rounded-2xl text-sm transition-all active:scale-[0.98]"
                >
                    {t('welcome:creerMonCompte')}
                </Button>
            </div>
        </div>
    );

    const renderLeftPanel = () => {
        if (view === 'home') {
            return null; // Handled separately for mobile/desktop
        }

        if (view === 'login') {
            return (
                <LoginForm
                    onBack={() => navigateToView('home')}
                    onSwitchToRegister={() => navigateToView('register')}
                    onSwitchToForgotPassword={() => navigateToView('forgot-password')}
                />
            );
        }

        if (view === 'register') {
            return (
                <RegisterForm onBack={() => navigateToView('home')} onSwitchToLogin={() => navigateToView('login')} />
            );
        }

        if (view === 'forgot-password') {
            return (
                <ForgotPasswordForm
                    onBack={() => navigateToView('login')}
                    onSwitchToLogin={() => navigateToView('login')}
                />
            );
        }

        if (view === 'reset-password') {
            return (
                <ResetPasswordForm
                    onBack={() => navigateToView('forgot-password')}
                    onSwitchToLogin={() => navigateToView('login')}
                    token={resetToken}
                />
            );
        }

        if (view === 'create-password') {
            return (
                <CreatePasswordForm
                    onBack={() => navigateToView('home')}
                    onSwitchToLogin={() => navigateToView('login')}
                    token={resetToken}
                />
            );
        }
    };

    // Mobile view for home
    if (view === 'home') {
        return (
            <>
                {/* Mobile layout */}
                <div className="mobile-only lg:hidden">{renderMobileHome()}</div>

                {/* Desktop layout */}
                <div className="desktop-only hidden lg:block h-screen bg-background p-12 overflow-y-auto">
                    <div className="grid grid-cols-2 h-full gap-8">
                        {renderDesktopHome()}
                        <HomeCarousel />
                    </div>
                </div>
            </>
        );
    }

    // Forms view (login, register, etc.)
    return (
        <div className="h-screen bg-background overflow-hidden lg:flex lg:flex-row lg:p-12 lg:gap-8">
            <div className="flex-1 min-h-0 h-full overflow-y-auto px-5 py-6 lg:p-0">
                {renderLeftPanel()}
            </div>
            <HomeCarousel className="hidden lg:block lg:flex-1" />
        </div>
    );
}
